import type { ChatAttachedImage } from "@/features/companion";

/**
 * Turn a hosted media URL into a ChatAttachedImage. Tries to fetch the bytes
 * so we can embed a base64 dataUrl for Claude vision; when the CDN blocks
 * cross-origin fetches (our own `cdn.wiserstudio.ai` does), we fall back to a
 * URL-only attachment. The thumbnail still renders via `<img src>` and the
 * chat pipeline appends the hosted URL to the message text so the agent still
 * has a reference.
 *
 * Pass `kind: "video"` to skip the fetch entirely — videos are attached as
 * URL-only references since Claude vision can't process MP4 frames anyway.
 */
export async function urlToAttachedImage(payload: {
  url: string;
  filename: string;
  mediaType: string;
  kind?: "image" | "video";
}): Promise<ChatAttachedImage> {
  const kind = payload.kind ?? "image";
  const base: ChatAttachedImage = {
    url: payload.url,
    filename: payload.filename,
    mediaType: payload.mediaType || (kind === "video" ? "video/mp4" : "image/jpeg"),
    type: kind,
  };
  if (kind === "video") return base;
  try {
    const res = await fetch(payload.url);
    if (!res.ok) return base;
    const blob = await res.blob();
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error ?? new Error("FileReader failed"));
      reader.readAsDataURL(blob);
    });
    return { ...base, dataUrl, mediaType: payload.mediaType || blob.type || "image/jpeg" };
  } catch {
    // CORS-blocked or network error — fall back to URL-only.
    return base;
  }
}
